import { MaterialCommunityIcons } from '@expo/vector-icons';
import { Colors } from '@gripp/shared-ui';
import * as DocumentPicker from 'expo-document-picker';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { TextInput } from 'react-native-paper';

interface Specification {
  key: string;
  value: string;
}

type SpecMap = Record<string, string>;

type ProductSpecificationsProps = {
  specifications: SpecMap | string | null | undefined;
  onSpecificationsChange: (specifications: SpecMap) => void;
};

export const ProductSpecifications = ({
  specifications,
  onSpecificationsChange,
}: ProductSpecificationsProps) => {
  const { t } = useTranslation();
  const csvRef = useRef<any>(null);
  const [rows, setRows] = useState<Specification[]>(() => toRows(specifications));

  // Update specs when specifications prop changes
  useEffect(() => {
    const nextRows = toRows(specifications);
    const incoming = rowsToObject(nextRows);
    const current = rowsToObject(rows);
    if (!shallowEqual(incoming, current)) {
      setRows(nextRows);
    }
  }, [specifications]);

  const updateRowState = (newRows: Specification[]) => {
    setRows(newRows);
    onSpecificationsChange(rowsToObject(newRows));
  };

  const addSpecification = () => {
    updateRowState([...rows, { key: '', value: '' }]);
  };

  const deleteSpecification = (index: number) => {
    const newRows = rows.filter((_, i) => i !== index);
    updateRowState(newRows);
  };

  const updateSpecification = (index: number, field: 'key' | 'value', value: string) => {
    const newRows = [...rows];
    newRows[index] = { ...newRows[index], [field]: value } as Specification;
    updateRowState(newRows);
  };

  const uploadData = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: 'text/csv',
        copyToCacheDirectory: true,
      });

      if (result.canceled) {
        return;
      }

      const file = result.assets[0];
      
      // Web-compatible file reading
      let fileContent: string;
      if (file.uri.startsWith('file://')) {
        // For mobile, we can use fetch to read the file
        const response = await fetch(file.uri);
        fileContent = await response.text();
      } else {
        // For web, use FileReader
        const response = await fetch(file.uri);
        const blob = await response.blob();
        fileContent = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.readAsText(blob);
        });
      }
      
      // Simple CSV parsing: First two columns are key,value. Optional header row.
      const lines = fileContent.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      const csvRows: Specification[] = [];
      lines.forEach((line, idx) => {
        // Skip header if present
        if (idx === 0 && (/specification/i.test(line) || /key/i.test(line))) return;
        const parts = line.split(',');
        if (parts.length < 2) return;
        let key = parts[0].trim();
        let value = parts.slice(1).join(',').trim(); // allow commas in value (simple approach)
        key = stripQuotes(key);
        value = stripQuotes(value);
        if (key && value) csvRows.push({ key, value });
      });
      
      // Update specifications with CSV data
      updateRowState(csvRows);
      Alert.alert(t('common.success'), t('product.specifications.upload_success'));
    } catch (error) {
      console.error('Upload error:', error);
      Alert.alert(t('common.error'), t('product.specifications.upload_error'));
    }
  };

  const downloadData = () => {
    // Check if there's data to download
    if (!rows || rows.length === 0 || !rows.some((spec: Specification) => spec.key.trim() && spec.value.trim())) {
      Alert.alert(t('common.info'), t('product.specifications.no_data_to_download'));
      return;
    }
    
    // Trigger the hidden CSVLink
    setTimeout(() => {
      csvRef.current?.link?.click();
    }, 50);
  };

  // Prepare CSV data for download
  const csvData = useMemo(() => {
    const valid = rows.filter((r) => r.key.trim() && r.value.trim());
    return valid.map((r) => ({ key: r.key.trim(), value: r.value.trim() }));
  }, [rows]);

  const csvHeaders = [
    { label: 'Specification', key: 'key' },
    { label: 'Value', key: 'value' },
  ];

  return (
    <View style={styles.card}>
      <View style={styles.cardHeader}>
        <Text style={styles.cardHeaderText}>{t('product.specifications.title')}</Text>
        <View style={styles.headerActions}>
          <TouchableOpacity style={styles.actionButton} onPress={uploadData}>
            <MaterialCommunityIcons name="upload" size={16} color={Colors.primaryDark} />
            <Text style={styles.actionButtonText}>{t('product.specifications.upload')}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.actionButton} onPress={downloadData}>
            <MaterialCommunityIcons name="download" size={16} color={Colors.primaryDark} />
            <Text style={styles.actionButtonText}>{t('product.specifications.download')}</Text>
          </TouchableOpacity>
          
          {/* Hidden CSVLink for download */}
          <CSVLink
            ref={csvRef}
            style={{ display: 'none' }}
            data={csvData}
            headers={csvHeaders}
            filename={`specifications_${new Date().getTime()}.csv`}
          />
        </View>
      </View>
      
      <ScrollView style={styles.specificationsList}>
        {(rows || []).map((spec, index) => (
          <View key={index} style={styles.specificationRow}>
            <TextInput
              mode="outlined"
              style={[
                styles.specificationInput,
                styles.specificationKeyInput,
              ]}
              value={spec.key || ''}
              onChangeText={(value) => updateSpecification(index, 'key', value)}
              placeholder={t('product.specifications.label_placeholder')}
              placeholderTextColor={Colors.grayDisabled}
              activeOutlineColor={Colors.black}
              outlineColor={Colors.tabInactive}
            />
            <TextInput
              mode="outlined"
              style={[
                styles.specificationInput,
                styles.specificationValueInput,
              ]}
              value={spec.value || ''}
              onChangeText={(value) => updateSpecification(index, 'value', value)}
              placeholder={t('product.specifications.value_placeholder')}
              placeholderTextColor={Colors.grayDisabled}
              activeOutlineColor={Colors.black}
              outlineColor={Colors.tabInactive}
            />
            <TouchableOpacity
              style={styles.deleteButton}
              onPress={() => deleteSpecification(index)}
            >
              <MaterialCommunityIcons name="delete-outline" size={20} color={Colors.grayDisabled} />
            </TouchableOpacity>
          </View>
        ))}
      </ScrollView>
      
      <TouchableOpacity style={styles.addButton} onPress={addSpecification}>
        <Text style={styles.addButtonText}>+ {t('product.specifications.add_more')}</Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: Colors.white,
    padding: 20,
    borderRadius: 8,
    gap: 20,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardHeaderText: {
    fontSize: 20,
    fontWeight: 'bold',
    color: Colors.black,
  },
  headerActions: {
    flexDirection: 'row',
    gap: 12,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  actionButtonText: {
    fontSize: 14,
    color: Colors.primaryDark,
    fontWeight: '500',
  },
  specificationsList: {
    maxHeight: 300,
  },
  specificationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  specificationInput: {
    height: 44,
    backgroundColor: Colors.white,
  },
  specificationKeyInput: {
    flex: 0.4, // 40% width
  },
  specificationValueInput: {
    flex: 0.6, // 60% width
  },
  deleteButton: {
    padding: 4,
  },
  addButton: {
    alignSelf: 'flex-start',
  },
  addButtonText: {
    fontSize: 14,
    color: Colors.primaryDark,
    fontWeight: '500',
  },
});

// Simple helpers
function toRows(specs: SpecMap | string | null | undefined): Specification[] {
  const base: Specification[] = [
    { key: '', value: '' },
    { key: '', value: '' },
    { key: '', value: '' },
  ];
  try {
    if (!specs) return base;
    let obj: any = specs;
    if (typeof specs === 'string') {
      if (!specs.trim()) return base;
      obj = JSON.parse(specs);
    }
    if (Array.isArray(obj)) {
      const arr = obj.map((it) => ({ key: String(it?.key ?? ''), value: String(it?.value ?? '') }));
      return ensureRows(arr);
    }
    if (typeof obj === 'object' && obj !== null) {
      const arr = Object.entries(obj).map(([k, v]) => ({ key: String(k), value: String(v) }));
      return ensureRows(arr);
    }
    return base;
  } catch {
    return base;
  }
}

function ensureRows(items: Specification[], min = 3): Specification[] {
  const out = [...items];
  while (out.length < min) out.push({ key: '', value: '' });
  return out;
}

function rowsToObject(items: Specification[]): SpecMap {
  const obj: SpecMap = {};
  items.forEach((it) => {
    const k = (it.key ?? '').trim();
    const v = (it.value ?? '').trim();
    if (k && v) obj[k] = v; // last one wins on duplicate keys
  });
  return obj;
}

function stripQuotes(s: string): string {
  if (s.startsWith('"') && s.endsWith('"')) return s.slice(1, -1);
  if (s.startsWith("'") && s.endsWith("'")) return s.slice(1, -1);
  return s;
}

function shallowEqual(a: Record<string, string>, b: Record<string, string>) {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    if (a[k] !== b[k]) return false;
  }
  return true;
}
